// db.js
// The data layer. Every screen reads from loadAll() and calls these mutation
// helpers. Each helper writes to Supabase; the existing domain logic
// (progression chain, obsComplete, rtStatus, etc.) stays in App as pure functions
// you call BEFORE writing, to decide what rows to insert/update.
//
// This file deliberately mirrors the in-memory actions you already have so the
// swap is mechanical: replace `update(s => ...)` bodies with calls to these.

import { supabase } from "./supabaseClient";

const today = () => new Date().toISOString().slice(0, 10);

/* ---------------- READ: load everything for the app ---------------- */
export async function loadAll() {
  const [emps, levels, quizzes, signoffs, observations, retrains, schedule, l4, history, log] =
    await Promise.all([
      supabase.from("employees").select("*").order("name"),
      supabase.from("ride_levels").select("*"),
      supabase.from("quizzes").select("*"),
      supabase.from("signoffs").select("*"),
      supabase.from("observations").select("*"),
      supabase.from("retrains").select("*").eq("cleared", false),
      supabase.from("schedule").select("*").eq("done", false),
      supabase.from("l4_checks").select("*"),
      supabase.from("history").select("*").order("created_at", { ascending: false }),
      supabase.from("activity_log").select("*").order("created_at", { ascending: false }).limit(100),
    ]);

  // Assemble into the same nested shape the UI already uses (employees[].levels, etc.)
  const byEmp = {};
  (emps.data || []).forEach((e) => {
    byEmp[e.id] = {
      id: e.id, name: e.name, startDate: e.start_date || "", archived: !!e.archived,
      department: e.department || "Attractions",
      jobTitle: e.job_title || "Ride Attendant",
      createdBy: e.created_by || "",
      levels: {}, retrain: {}, observations: {}, l4Checks: {}, schedule: [], history: [],
    };
  });
  (levels.data || []).forEach((r) => { if (byEmp[r.employee_id]) byEmp[r.employee_id].levels[r.ride] = r.level; });
  (observations.data || []).forEach((o) => {
    const e = byEmp[o.employee_id]; if (!e) return;
    (e.observations[o.ride] = e.observations[o.ride] || []).push({
      id: o.id, kind: o.context === "ttt" || o.context === "ttt_l4" ? "ttt" : undefined,
      forL4: o.for_l4, slot: o.slot, result: o.result, observer: o.observer,
      agmObserved: o.agm_observed, date: o.obs_date, note: o.note,
    });
  });
  (retrains.data || []).forEach((rt) => {
    const e = byEmp[rt.employee_id]; if (!e) return;
    e.retrain[rt.ride] = { id: rt.id, level: rt.level, ride: rt.ride, openedBy: rt.opened_by,
      openedDate: rt.opened_date, needDifferentAgm: rt.need_diff_agm, observations: [] };
  });
  // attach RT observations (context = 'rt')
  (observations.data || []).filter((o) => o.context === "rt").forEach((o) => {
    const e = byEmp[o.employee_id]; const rt = e && e.retrain[o.ride];
    if (rt) rt.observations.push({ id: o.id, result: o.result, observer: o.observer, date: o.obs_date, note: o.note });
  });
  (l4.data || []).forEach((c) => {
    const e = byEmp[c.employee_id]; if (!e) return;
    (e.l4Checks[c.ride] = e.l4Checks[c.ride] || []).push({ date: c.check_date, ready: c.ready, by: c.by_name, note: c.note });
  });
  (schedule.data || []).forEach((s) => {
    const e = byEmp[s.employee_id]; if (!e) return;
    e.schedule.push({ id: s.id, ride: s.ride, targetLevel: s.target_level, date: s.due_date || "", note: s.note, assignedTo: s.assigned_to || null });
  });
  (history.data || []).forEach((h) => {
    const e = byEmp[h.employee_id]; if (!e) return;
    e.history.push({ date: h.entry_date, text: h.text });
  });

  const quizRows = (quizzes.data || []).map((q) => ({
    id: q.id, empId: q.employee_id, empName: byEmp[q.employee_id]?.name || "", ride: q.ride,
    targetLevel: q.target_level, status: q.status, score: q.score, attempts: q.attempts,
    missedTopics: q.missed_topics || [], supervisor: q.supervisor, sentDate: q.sent_date,
    completedDate: q.completed_date, questions: q.questions || [],
    attestation: q.attestation || null, attestedAt: q.attested_at || null,
    answers: q.answers || null, attemptHistory: q.attempt_history || [],
  }));
  const signoffRows = (signoffs.data || []).map((s) => ({
    id: s.id, empId: s.employee_id, empName: s.emp_name, ride: s.ride, targetLevel: s.target_level,
    kind: s.kind, status: s.status, requiredRoles: s.required_roles || [], signatures: s.signatures || [],
    missedTopics: s.missed_topics || [], note: s.note,
    date: s.created_at ? s.created_at.slice(0, 10) : null,
  }));

  return {
    employees: Object.values(byEmp),
    quizzes: quizRows,
    signoffs: signoffRows,
    log: (log.data || []).map((l) => ({ date: l.log_date, text: l.text, type: l.type })),
    notifications: [], // notifications can be derived client-side or added as a table later
  };
}

/* ---------------- WRITE helpers (mutations) ----------------
   Call loadAll() again after any of these, or use Supabase realtime. */

export async function addEmployee(name, startDate, actor) {
  const { data, error } = await supabase.from("employees")
    .insert({ name, start_date: startDate || null, created_by: actor?.name || "" }).select().single();
  if (error) throw error;
  // seed level rows at '0'
  const RIDES = ["slide", "tornado", "dragon", "kiddieKart", "road", "sprint", "euro"];
  await supabase.from("ride_levels").insert(RIDES.map((ride) => ({ employee_id: data.id, ride, level: "0" })));
  await logActivity(`Added employee: ${name}`, "new");
  return data;
}

export async function setLevel(employeeId, ride, level) {
  await supabase.from("ride_levels").upsert({ employee_id: employeeId, ride, level }, { onConflict: "employee_id,ride" });
}

export async function setArchived(employeeId, archived) {
  await supabase.from("employees").update({ archived }).eq("id", employeeId);
}

export async function deleteEmployee(employeeId) {
  // Admin only (enforced by RLS). Cascades remove child rows.
  await supabase.from("employees").delete().eq("id", employeeId);
}

export async function addHistory(employeeId, text) {
  await supabase.from("history").insert({ employee_id: employeeId, text, entry_date: today() });
}
export async function logActivity(text, type = "info") {
  await supabase.from("activity_log").insert({ text, type, log_date: today() });
}

export async function createQuiz(q) {
  await supabase.from("quizzes").insert({
    employee_id: q.empId, ride: q.ride, target_level: q.targetLevel, status: "sent",
    supervisor: q.supervisor, sent_date: today(), questions: q.questions, attempts: 0,
  });
}
export async function updateQuiz(id, fields) {
  const map = {
    status: fields.status, score: fields.score, attempts: fields.attempts,
    missed_topics: fields.missedTopics, completed_date: fields.completedDate,
    questions: fields.questions,
    attestation: fields.attestation, attested_at: fields.attestedAt,
  };
  Object.keys(map).forEach((k) => map[k] === undefined && delete map[k]);
  await supabase.from("quizzes").update(map).eq("id", id);
}

export async function createSignoff(so) {
  await supabase.from("signoffs").insert({
    employee_id: so.empId, emp_name: so.empName, ride: so.ride, target_level: so.targetLevel,
    kind: so.kind, status: "open", required_roles: so.requiredRoles, signatures: so.signatures || [],
    missed_topics: so.missedTopics || [], note: so.note || "",
  });
}
export async function updateSignoff(id, fields) {
  const map = { status: fields.status, signatures: fields.signatures };
  Object.keys(map).forEach((k) => map[k] === undefined && delete map[k]);
  await supabase.from("signoffs").update(map).eq("id", id);
}

export async function addObservation(o) {
  await supabase.from("observations").insert({
    employee_id: o.empId, ride: o.ride, context: o.context, slot: o.slot || null,
    result: o.result, observer: o.observer || {}, agm_observed: !!o.agmObserved,
    for_l4: !!o.forL4, trainee_trained: o.traineeTrained || "", note: o.note || "", obs_date: today(),
  });
}

export async function openRetrain(employeeId, ride, level, actor) {
  await supabase.from("retrains").insert({
    employee_id: employeeId, ride, level, opened_by: actor?.name || "", opened_date: today(), cleared: false,
  });
}
export async function updateRetrain(id, fields) {
  const map = { need_diff_agm: fields.needDifferentAgm, cleared: fields.cleared };
  Object.keys(map).forEach((k) => map[k] === undefined && delete map[k]);
  await supabase.from("retrains").update(map).eq("id", id);
}

export async function addSchedule(employeeId, ride, targetLevel, dueDate, note) {
  await supabase.from("schedule").insert({ employee_id: employeeId, ride, target_level: targetLevel, due_date: dueDate || null, note: note || "" });
}
export async function removeSchedule(id) {
  await supabase.from("schedule").update({ done: true }).eq("id", id);
}

export async function addL4Check(employeeId, ride, ready, actor, note) {
  await supabase.from("l4_checks").insert({ employee_id: employeeId, ride, ready, by_name: actor?.name || "", note: note || "", check_date: today() });
}

/* ============================================================
   syncDiff(prev, next)
   Compares two whole-app snapshots and writes only what changed.
   This lets the existing UI keep its update(state => ...) actions
   unchanged while persisting relationally to Supabase.
   ============================================================ */
const RIDES_ALL = ["slide", "tornado", "dragon", "kiddieKart", "road", "sprint", "euro"];

export async function syncDiff(prev, next) {
  const ops = [];
  const pById = Object.fromEntries((prev?.employees || []).map((e) => [e.id, e]));
  const nById = Object.fromEntries((next?.employees || []).map((e) => [e.id, e]));

  // --- New employees ---
  for (const e of next.employees) {
    if (!pById[e.id]) {
      // create employee row + level rows + any history
      ops.push((async () => {
        // If the id looks client-generated (emp-...), let DB assign a uuid by inserting w/o id
        const { data, error } = await supabase.from("employees")
          .insert({ name: e.name, start_date: e.startDate || null, archived: !!e.archived, department: e.department || "Attractions", job_title: e.jobTitle || "Ride Attendant", created_by: e.createdBy || "" })
          .select().single();
        if (error) throw error;
        const realId = data.id;
        await supabase.from("ride_levels").insert(
          RIDES_ALL.map((ride) => ({ employee_id: realId, ride, level: e.levels?.[ride] || "0" }))
        );
        if (e.history?.length) {
          await supabase.from("history").insert(e.history.map((h) => ({ employee_id: realId, text: h.text, entry_date: h.date })));
        }
      })());
    }
  }

  // --- Existing employees: detect field/level/child changes ---
  for (const e of next.employees) {
    const p = pById[e.id];
    if (!p) continue;
    // employee fields
    if (p.archived !== e.archived || p.name !== e.name || p.startDate !== e.startDate || p.department !== e.department || p.jobTitle !== e.jobTitle) {
      ops.push(supabase.from("employees").update({ name: e.name, start_date: e.startDate || null, archived: !!e.archived, department: e.department || "Attractions", job_title: e.jobTitle || "Ride Attendant" }).eq("id", e.id));
    }
    // levels
    for (const ride of RIDES_ALL) {
      if ((p.levels?.[ride] || "0") !== (e.levels?.[ride] || "0")) {
        ops.push(supabase.from("ride_levels").upsert({ employee_id: e.id, ride, level: e.levels[ride] || "0" }, { onConflict: "employee_id,ride" }));
      }
    }
    // new history rows (compare counts; new ones are unshifted to front)
    const pHist = p.history || [], nHist = e.history || [];
    if (nHist.length > pHist.length) {
      const added = nHist.slice(0, nHist.length - pHist.length);
      ops.push(supabase.from("history").insert(added.map((h) => ({ employee_id: e.id, text: h.text, entry_date: h.date }))));
    }
    // retrain open/close
    const pR = p.retrain || {}, nR = e.retrain || {};
    for (const ride of RIDES_ALL) {
      if (!pR[ride] && nR[ride]) {
        ops.push(supabase.from("retrains").insert({ employee_id: e.id, ride, level: nR[ride].level, opened_by: nR[ride].openedBy || "", opened_date: nR[ride].openedDate || undefined, cleared: false }));
      } else if (pR[ride] && !nR[ride]) {
        ops.push(supabase.from("retrains").update({ cleared: true }).eq("employee_id", e.id).eq("ride", ride).eq("cleared", false));
      } else if (pR[ride] && nR[ride]) {
        // observation appended to RT, or needDifferentAgm changed
        const pObs = pR[ride].observations || [], nObs = nR[ride].observations || [];
        if (nObs.length > pObs.length) {
          const added = nObs.slice(pObs.length);
          ops.push(supabase.from("observations").insert(added.map((o) => ({ employee_id: e.id, ride, context: "rt", result: o.result, observer: o.observer || {}, note: o.note || "", obs_date: o.date }))));
        }
        if (JSON.stringify(pR[ride].needDifferentAgm) !== JSON.stringify(nR[ride].needDifferentAgm)) {
          ops.push(supabase.from("retrains").update({ need_diff_agm: nR[ride].needDifferentAgm }).eq("employee_id", e.id).eq("ride", ride).eq("cleared", false));
        }
      }
    }
    // new observations (non-RT) per ride
    const pO = p.observations || {}, nO = e.observations || {};
    for (const ride of RIDES_ALL) {
      const pl = pO[ride] || [], nl = nO[ride] || [];
      if (nl.length > pl.length) {
        const added = nl.slice(pl.length);
        ops.push(supabase.from("observations").insert(added.map((o) => ({
          employee_id: e.id, ride,
          context: o.kind === "ttt" ? (o.forL4 ? "ttt_l4" : "ttt") : "obs",
          slot: o.slot || null, result: o.result, observer: o.observer || {},
          agm_observed: !!o.agmObserved, for_l4: !!o.forL4, note: o.note || "", obs_date: o.date,
        }))));
      }
    }
    // new schedule rows + detect assignedTo changes on existing rows
    const pS = p.schedule || [], nS = e.schedule || [];
    const schedById = Object.fromEntries(pS.filter((s) => s.id).map((s) => [s.id, s]));
    const pSkeys = new Set(pS.map((s) => s.ride + "|" + s.targetLevel));
    for (const s of nS) {
      if (!pSkeys.has(s.ride + "|" + s.targetLevel)) {
        ops.push(supabase.from("schedule").insert({ employee_id: e.id, ride: s.ride, target_level: s.targetLevel, due_date: s.date || null, note: s.note || "", assigned_to: s.assignedTo || null }));
      } else if (s.id && schedById[s.id] && (schedById[s.id].assignedTo || null) !== (s.assignedTo || null)) {
        // assignedTo changed on an existing row
        ops.push(supabase.from("schedule").update({ assigned_to: s.assignedTo || null }).eq("id", s.id));
      }
    }
    // removed schedule rows -> mark done
    const nSkeys = new Set(nS.map((s) => s.ride + "|" + s.targetLevel));
    for (const s of pS) {
      if (!nSkeys.has(s.ride + "|" + s.targetLevel) && s.id) {
        ops.push(supabase.from("schedule").update({ done: true }).eq("id", s.id));
      }
    }
    // new l4 checks
    const pL = p.l4Checks || {}, nL = e.l4Checks || {};
    for (const ride of RIDES_ALL) {
      const pc = pL[ride] || [], nc = nL[ride] || [];
      if (nc.length > pc.length) {
        const added = nc.slice(pc.length);
        ops.push(supabase.from("l4_checks").insert(added.map((c) => ({ employee_id: e.id, ride, ready: c.ready, by_name: c.by || "", note: c.note || "", check_date: c.date }))));
      }
    }
  }

  // --- Deleted employees (admin) ---
  for (const e of prev.employees) {
    if (!nById[e.id]) ops.push(supabase.from("employees").delete().eq("id", e.id));
  }

  // --- Quizzes ---
  const pq = Object.fromEntries((prev.quizzes || []).map((q) => [q.id, q]));
  const nq = Object.fromEntries((next.quizzes || []).map((q) => [q.id, q]));
  for (const q of next.quizzes) {
    if (!pq[q.id]) {
      ops.push(supabase.from("quizzes").insert({ id: isUuid(q.id) ? q.id : undefined, employee_id: q.empId, ride: q.ride, target_level: q.targetLevel, status: q.status, supervisor: q.supervisor || "", sent_date: q.sentDate || null, questions: q.questions || [], attempts: q.attempts || 0, missed_topics: q.missedTopics || [], attestation: q.attestation || null, attested_at: q.attestedAt || null, answers: q.answers || null, attempt_history: q.attemptHistory || [] }));
    } else {
      const o = pq[q.id];
      // The attestation signature is written once and never overwritten.
      const attestationAdded = !o.attestation && !!q.attestation;
      if (o.status !== q.status || o.score !== q.score || o.attempts !== q.attempts || o.completedDate !== q.completedDate || attestationAdded) {
        ops.push(supabase.from("quizzes").update({ status: q.status, score: q.score, attempts: q.attempts, missed_topics: q.missedTopics || [], completed_date: q.completedDate || null, questions: q.questions || [], attestation: q.attestation || null, attested_at: q.attestedAt || null, answers: q.answers || null, attempt_history: q.attemptHistory || [] }).eq("id", q.id));
      }
    }
  }
  // Deletions: any quiz in prev but not in next gets removed from DB
  for (const q of (prev.quizzes || [])) {
    if (!nq[q.id] && isUuid(q.id)) {
      ops.push(supabase.from("quizzes").delete().eq("id", q.id));
    }
  }

  // --- Sign-offs ---
  const ps = Object.fromEntries((prev.signoffs || []).map((s) => [s.id, s]));
  const ns = Object.fromEntries((next.signoffs || []).map((s) => [s.id, s]));
  for (const s of next.signoffs) {
    if (!ps[s.id]) {
      ops.push(supabase.from("signoffs").insert({ id: isUuid(s.id) ? s.id : undefined, employee_id: s.empId, emp_name: s.empName, ride: s.ride, target_level: s.targetLevel, kind: s.kind, status: s.status, required_roles: s.requiredRoles || [], signatures: s.signatures || [], missed_topics: s.missedTopics || [], note: s.note || "" }));
    } else if (JSON.stringify(ps[s.id].signatures) !== JSON.stringify(s.signatures) || ps[s.id].status !== s.status) {
      ops.push(supabase.from("signoffs").update({ signatures: s.signatures, status: s.status }).eq("id", s.id));
    }
  }
  // Deletions: any signoff in prev but not in next gets removed from DB
  for (const s of (prev.signoffs || [])) {
    if (!ns[s.id] && isUuid(s.id)) {
      ops.push(supabase.from("signoffs").delete().eq("id", s.id));
    }
  }

  // --- Activity log (new entries unshifted to front) ---
  const addedLog = (next.log || []).length - (prev.log || []).length;
  if (addedLog > 0) {
    const added = next.log.slice(0, addedLog);
    ops.push(supabase.from("activity_log").insert(added.map((l) => ({ text: l.text, type: l.type || "info", log_date: l.date }))));
  }

  await Promise.all(ops);
}

function isUuid(s) {
  return typeof s === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
}

/* ============================================================
   createUserLogin — calls the admin-create-user Edge Function.
   Only works when the signed-in caller is an Admin (enforced
   server-side inside the function). Creates the login, sets the
   role, and links to an employee record by name (optional).
   ============================================================ */
export async function createUserLogin({ username, real_email, password, full_name, title, role, start_date }) {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess?.session?.access_token;
  const url = import.meta.env.VITE_SUPABASE_URL;
  const res = await fetch(`${url}/functions/v1/admin-create-user`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
      "apikey": import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ username, real_email, password, full_name, title, role, start_date }),
  });
  let out = {};
  try { out = await res.json(); } catch (_) {}
  if (!res.ok || out.error) return { ok: false, message: out.error || `Failed (${res.status})` };
  return { ok: true, ...out };
}

/* ============================================================
   Admin user management — calls admin-user-mgmt Edge Function.
   All actions verified server-side as Admin.
   ============================================================ */
async function callAdminFn(action, payload = {}) {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess?.session?.access_token;
  const url = import.meta.env.VITE_SUPABASE_URL;
  const res = await fetch(`${url}/functions/v1/admin-user-mgmt`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
      "apikey": import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ action, ...payload }),
  });
  let out = {};
  try { out = await res.json(); } catch (_) {}
  if (!res.ok || out.error) return { ok: false, message: out.error || `Failed (${res.status})` };
  return { ok: true, ...out };
}

export const listUsers       = ()         => callAdminFn("list");
export const updateUserAdmin = (payload)  => callAdminFn("update", payload);
export const deleteUserAdmin = (user_id)  => callAdminFn("delete", { user_id });
export const passwordResetLink = (email)  => callAdminFn("reset_link", { email });

/* Bulk import (admin) */
export async function bulkImport({ rows, temp_password }) {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess?.session?.access_token;
  const url = import.meta.env.VITE_SUPABASE_URL;
  const res = await fetch(`${url}/functions/v1/admin-bulk-import`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
      "apikey": import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ rows, temp_password }),
  });
  let out = {};
  try { out = await res.json(); } catch (_) {}
  if (!res.ok || out.error) return { ok: false, message: out.error || `Failed (${res.status})` };
  return { ok: true, ...out };
}

/* Load list of users who can be assigned as trainers. Anyone with role ≥ Trainer
   in the role hierarchy: Trainer, Manager, AGM/GM, Admin. */
export async function loadAssignableTrainers() {
  const { data, error } = await supabase
    .from("profiles")
    .select("id, full_name, role, username")
    .in("role", ["trainer", "manager", "agm_gm", "admin"])
    .order("full_name", { ascending: true });
  if (error) return { ok: false, message: error.message };
  return { ok: true, trainers: data || [] };
}

/* Load all active quiz questions */
export async function loadQuizQuestions() {
  const { data, error } = await supabase
    .from("quiz_questions")
    .select("id, text, choices, correct, tags, mandatory")
    .eq("archived", false)
    .order("created_at", { ascending: true });
  if (error) return { ok: false, message: error.message };
  return { ok: true, questions: data || [] };
}

/* Create a new question (AGM/GM+ only via RLS) */
export async function createQuizQuestion({ text, choices, correct, tags, mandatory }) {
  const { data, error } = await supabase
    .from("quiz_questions")
    .insert({ text, choices, correct, tags: tags || [], mandatory: !!mandatory })
    .select().single();
  if (error) return { ok: false, message: error.message };
  return { ok: true, question: data };
}

/* Update a question */
export async function updateQuizQuestion(id, { text, choices, correct, tags, mandatory }) {
  const update = {};
  if (text !== undefined) update.text = text;
  if (choices !== undefined) update.choices = choices;
  if (correct !== undefined) update.correct = correct;
  if (tags !== undefined) update.tags = tags;
  if (mandatory !== undefined) update.mandatory = !!mandatory;
  const { data, error } = await supabase
    .from("quiz_questions").update(update).eq("id", id).select().single();
  if (error) return { ok: false, message: error.message };
  return { ok: true, question: data };
}

/* Archive a question (soft delete) */
export async function archiveQuizQuestion(id) {
  const { error } = await supabase
    .from("quiz_questions").update({ archived: true }).eq("id", id);
  if (error) return { ok: false, message: error.message };
  return { ok: true };
}

/* Enable/disable a user's login. Auth user preserved. */
export async function setUserActive(user_id, active) {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess?.session?.access_token;
  const url = import.meta.env.VITE_SUPABASE_URL;
  const res = await fetch(`${url}/functions/v1/admin-set-user-active`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
      "apikey": import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ user_id, active }),
  });
  let out = {};
  try { out = await res.json(); } catch (_) {}
  if (!res.ok || out.error) return { ok: false, message: out.error || `Failed (${res.status})` };
  return { ok: true, ...out };
}

/* Load all active checklist items (used for the L2 obs UI + editor) */
export async function loadObsChecklists() {
  const { data, error } = await supabase
    .from("obs_checklists")
    .select("id, ride, category, item, sort_order, progression")
    .eq("archived", false)
    .order("ride")
    .order("sort_order");
  if (error) return { ok: false, message: error.message };
  return { ok: true, items: data || [] };
}

/* Create a new checklist item (AGM/GM+ only via RLS) */
export async function createObsChecklistItem({ ride, category, item, sort_order, progression }) {
  const { data, error } = await supabase
    .from("obs_checklists")
    .insert({ ride, category: category || "", item, sort_order: sort_order || 0, progression: progression || "t_to_l2" })
    .select().single();
  if (error) return { ok: false, message: error.message };
  return { ok: true, item: data };
}

export async function updateObsChecklistItem(id, fields) {
  const update = {};
  ["ride", "category", "item", "sort_order", "progression"].forEach((k) => {
    if (fields[k] !== undefined) update[k] = fields[k];
  });
  const { data, error } = await supabase
    .from("obs_checklists").update(update).eq("id", id).select().single();
  if (error) return { ok: false, message: error.message };
  return { ok: true, item: data };
}

export async function archiveObsChecklistItem(id) {
  const { error } = await supabase
    .from("obs_checklists").update({ archived: true }).eq("id", id);
  if (error) return { ok: false, message: error.message };
  return { ok: true };
}

/* Load L2 observations for an employee (history view) */
export async function loadL2Observations(employee_id) {
  const { data, error } = await supabase
    .from("l2_observations")
    .select("*")
    .eq("employee_id", employee_id)
    .order("created_at", { ascending: false });
  if (error) return { ok: false, message: error.message };
  return { ok: true, observations: data || [] };
}

/* Insert an L2 observation. Server enforces Manager+ via RLS. */
export async function createL2Observation(payload) {
  const { data, error } = await supabase
    .from("l2_observations")
    .insert(payload)
    .select().single();
  if (error) return { ok: false, message: error.message };
  return { ok: true, observation: data };
}

/* Load all training modules (ordered by sort_order) */
export async function loadTrainingModules() {
  // Select all columns so callers get section, description, workflow flags, etc.
  const { data, error } = await supabase
    .from("training_modules")
    .select("*")
    .order("sort_order");
  if (error) return { ok: false, message: error.message };
  return { ok: true, modules: data || [] };
}

/* Update a training module (admin only, enforced by RLS).
   Accepts any editable column. Whitelist below prevents accidental writes to
   generated/system columns (id, key, sort_order, timestamps, updated_by). */
export async function updateTrainingModule(key, fields) {
  const EDITABLE = new Set([
    "title", "body_md",
    "section", "description", "archived",
    "document_path",
    "has_quiz", "quiz_question_count",
    "requires_practical", "practical_items",
    "requires_competency_verification",
    "due_days_after_assignment",
    // Deliberately NOT accepting: category (deprecated - use section instead)
  ]);
  const update = {};
  for (const [k, v] of Object.entries(fields || {})) {
    if (EDITABLE.has(k)) update[k] = v;
  }
  update.updated_at = new Date().toISOString();
  const { data, error } = await supabase
    .from("training_modules")
    .update(update)
    .eq("key", key)
    .select().single();
  if (error) return { ok: false, message: error.message };
  return { ok: true, module: data };
}

/* ============================================================
   EMPLOYEE DOCUMENTS
   ============================================================ */

const EMP_DOCS_BUCKET = "employee-documents";

/* Load all (non-deleted) documents for an employee */
export async function loadEmployeeDocuments(employeeId, opts = {}) {
  const { includeDeleted = false } = opts;
  let q = supabase
    .from("employee_documents")
    .select("id, employee_id, category, filename, storage_path, description, size_bytes, mime_type, uploaded_by, uploaded_at, deleted_at, deleted_by, rides, issued_at, expires_at, card_number")
    .eq("employee_id", employeeId)
    .order("uploaded_at", { ascending: false });
  if (!includeDeleted) q = q.is("deleted_at", null);
  const { data, error } = await q;
  if (error) return { ok: false, message: error.message };
  return { ok: true, documents: data || [] };
}

/* Load all (non-deleted) documents across all employees. Used by Reports.
   Returns a flat array — caller groups/filters as needed. */
export async function loadAllEmployeeDocuments() {
  const { data, error } = await supabase
    .from("employee_documents")
    .select("id, employee_id, category, filename, storage_path, description, rides, issued_at, expires_at, card_number, uploaded_at")
    .is("deleted_at", null)
    .order("expires_at", { ascending: true, nullsFirst: false });
  if (error) return { ok: false, message: error.message };
  return { ok: true, documents: data || [] };
}

/* Load non-deleted documents in the given categories across all employees.
   Used by the PL202 report. Pages through results so the report never
   silently truncates at the PostgREST row limit. */
export async function loadCredentialDocuments(categories) {
  const PAGE = 1000;
  const all = [];
  for (let from = 0; ; from += PAGE) {
    let q = supabase
      .from("employee_documents")
      .select("id, employee_id, category, filename, storage_path, description, rides, issued_at, expires_at, card_number, uploaded_at")
      .is("deleted_at", null)
      .order("uploaded_at", { ascending: false })
      .order("id", { ascending: true })
      .range(from, from + PAGE - 1);
    if (Array.isArray(categories) && categories.length) q = q.in("category", categories);
    const { data, error } = await q;
    if (error) return { ok: false, message: error.message };
    all.push(...(data || []));
    if (!data || data.length < PAGE) break;
  }
  return { ok: true, documents: all };
}

/* Upload a document. file is a File or Blob; category, optional description,
   optional rides array (e.g. ['sprint', 'road'] — empty array = employee-level),
   optional issued_at and expires_at (YYYY-MM-DD strings — used for time-bound credentials);
   optional cardNumber (e.g. Work Card #, Health Card #) */
export async function uploadEmployeeDocument(employeeId, file, category, description, rides, issuedAt, expiresAt, cardNumber) {
  if (!file) return { ok: false, message: "No file selected" };
  if (file.size > 25 * 1024 * 1024) return { ok: false, message: "File exceeds 25 MB limit" };

  // Sanitize filename and build a stable storage path
  const safeName = (file.name || "document").replace(/[^a-zA-Z0-9._-]+/g, "_").slice(0, 200);
  const ext = safeName.includes(".") ? safeName.slice(safeName.lastIndexOf(".")) : "";
  // Use a random uuid in the path so multiple uploads of the same filename don't collide
  const docId = (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36));
  const storagePath = `emp_${employeeId}/${docId}${ext}`;

  // Upload to storage
  const uploadRes = await supabase.storage.from(EMP_DOCS_BUCKET).upload(storagePath, file, {
    cacheControl: "3600",
    upsert: false,
    contentType: file.type || "application/octet-stream",
  });
  if (uploadRes.error) return { ok: false, message: "Upload failed: " + uploadRes.error.message };

  // Record in DB
  const { data: { user } = {} } = await supabase.auth.getUser();
  const { data, error } = await supabase
    .from("employee_documents")
    .insert({
      employee_id: employeeId,
      category,
      filename: safeName,
      storage_path: storagePath,
      description: description || null,
      size_bytes: file.size,
      mime_type: file.type || "application/octet-stream",
      uploaded_by: user?.id || null,
      rides: Array.isArray(rides) ? rides : [],
      issued_at: issuedAt || null,
      expires_at: expiresAt || null,
      card_number: cardNumber || null,
    })
    .select().single();
  if (error) {
    // Best-effort: try to remove the orphaned storage file
    await supabase.storage.from(EMP_DOCS_BUCKET).remove([storagePath]).catch(() => {});
    return { ok: false, message: "DB insert failed: " + error.message };
  }
  return { ok: true, document: data };
}

/* Update editable metadata on an existing document (manager+).
   Fields not provided are left unchanged. The file itself, filename,
   upload date, and uploaded_by are NEVER changed by this function. */
export async function updateEmployeeDocument(docId, { category, description, rides, issuedAt, expiresAt, cardNumber }) {
  const patch = {};
  if (category !== undefined) patch.category = category;
  if (description !== undefined) patch.description = description || null;
  if (rides !== undefined) patch.rides = Array.isArray(rides) ? rides : [];
  // Dates: empty string → null (clear the field); explicit value → set; undefined → leave alone
  if (issuedAt !== undefined) patch.issued_at = issuedAt || null;
  if (expiresAt !== undefined) patch.expires_at = expiresAt || null;
  if (cardNumber !== undefined) patch.card_number = cardNumber || null;
  if (Object.keys(patch).length === 0) return { ok: true, document: null };
  const { data, error } = await supabase
    .from("employee_documents")
    .update(patch)
    .eq("id", docId)
    .select().single();
  if (error) return { ok: false, message: error.message };
  return { ok: true, document: data };
}

/* Soft-delete a document (manager+) */
export async function softDeleteEmployeeDocument(docId) {
  const { data: { user } = {} } = await supabase.auth.getUser();
  const { data, error } = await supabase
    .from("employee_documents")
    .update({
      deleted_at: new Date().toISOString(),
      deleted_by: user?.id || null,
    })
    .eq("id", docId)
    .select().single();
  if (error) return { ok: false, message: error.message };
  return { ok: true, document: data };
}

/* Restore a soft-deleted document (manager+) */
export async function restoreEmployeeDocument(docId) {
  const { data, error } = await supabase
    .from("employee_documents")
    .update({ deleted_at: null, deleted_by: null })
    .eq("id", docId)
    .select().single();
  if (error) return { ok: false, message: error.message };
  return { ok: true, document: data };
}

/* Hard-delete a document (admin only). Removes storage file + DB row. */
export async function hardDeleteEmployeeDocument(docId) {
  // First fetch the row to get the storage path
  const { data: row, error: fetchErr } = await supabase
    .from("employee_documents")
    .select("storage_path")
    .eq("id", docId)
    .single();
  if (fetchErr) return { ok: false, message: fetchErr.message };

  // Delete storage file (best effort — if it doesn't exist, we still proceed)
  await supabase.storage.from(EMP_DOCS_BUCKET).remove([row.storage_path]).catch(() => {});

  // Delete DB row
  const { error } = await supabase.from("employee_documents").delete().eq("id", docId);
  if (error) return { ok: false, message: error.message };
  return { ok: true };
}

/* Get a time-limited signed URL for viewing/downloading a document.
   expiresInSeconds defaults to 60 seconds (short-lived for security). */
export async function getEmployeeDocumentUrl(storagePath, expiresInSeconds = 60) {
  const { data, error } = await supabase.storage
    .from(EMP_DOCS_BUCKET)
    .createSignedUrl(storagePath, expiresInSeconds);
  if (error) return { ok: false, message: error.message };
  return { ok: true, url: data.signedUrl };
}

/* ============================================================
   COURSES (self-paced training)
   ============================================================ */
const COURSE_IMAGES_BUCKET = "course-images";
const COURSE_AUDIO_BUCKET  = "course-audio";

/* Load all active (non-archived) courses */
export async function loadCourses() {
  const { data, error } = await supabase
    .from("training_courses")
    .select("*")
    .eq("archived", false)
    .order("title", { ascending: true });
  if (error) return { ok: false, message: error.message };
  return { ok: true, courses: data || [] };
}

/* Load progress for a specific employee across all courses */
export async function loadCourseProgressForEmployee(employeeId) {
  const { data, error } = await supabase
    .from("course_progress")
    .select("*")
    .eq("employee_id", employeeId);
  if (error) return { ok: false, message: error.message };
  return { ok: true, progress: data || [] };
}

/* Get or create a progress row for (course, employee) */
export async function ensureCourseProgress(courseId, employeeId) {
  // Try to fetch existing first
  const existing = await supabase
    .from("course_progress")
    .select("*")
    .eq("course_id", courseId)
    .eq("employee_id", employeeId)
    .maybeSingle();
  if (existing.data) return { ok: true, progress: existing.data };
  if (existing.error && existing.error.code !== "PGRST116") {
    return { ok: false, message: existing.error.message };
  }
  // Create
  const { data, error } = await supabase
    .from("course_progress")
    .insert({
      course_id: courseId,
      employee_id: employeeId,
      current_slide_index: 0,
      slides_viewed: [],
      knowledge_checks: {},
    })
    .select().single();
  if (error) return { ok: false, message: error.message };
  return { ok: true, progress: data };
}

/* Update progress (called frequently as the trainee advances) */
export async function updateCourseProgress(progressId, patch) {
  const update = { ...patch, last_active_at: new Date().toISOString() };
  const { data, error } = await supabase
    .from("course_progress")
    .update(update)
    .eq("id", progressId)
    .select().single();
  if (error) return { ok: false, message: error.message };
  return { ok: true, progress: data };
}

/* Get signed URL for a course image (longer expiry — course media is fine to expose for a session) */
export async function getCourseImageUrl(storagePath, expiresInSeconds = 3600) {
  const { data, error } = await supabase.storage
    .from(COURSE_IMAGES_BUCKET)
    .createSignedUrl(storagePath, expiresInSeconds);
  if (error) return { ok: false, message: error.message };
  return { ok: true, url: data.signedUrl };
}

/* Get signed URL for a course audio file */
export async function getCourseAudioUrl(storagePath, expiresInSeconds = 3600) {
  const { data, error } = await supabase.storage
    .from(COURSE_AUDIO_BUCKET)
    .createSignedUrl(storagePath, expiresInSeconds);
  if (error) return { ok: false, message: error.message };
  return { ok: true, url: data.signedUrl };
}

/* ============================================================
   MODULES (unified: ride + service_tech + restaurant + general)
   ============================================================ */
const MODULE_DOCS_BUCKET = "required-training-docs";  // reuse existing bucket for optional PDF attachments

/* Load all training modules (all sections), optionally filtered.
   opts: { section?, excludeSection?, includeArchived? } */
export async function loadModules(opts = {}) {
  let q = supabase.from("training_modules").select("*").order("title", { ascending: true });
  if (opts.section) q = q.eq("section", opts.section);
  if (opts.excludeSection) q = q.neq("section", opts.excludeSection);
  if (!opts.includeArchived) q = q.eq("archived", false);
  const { data, error } = await q;
  if (error) return { ok: false, message: error.message };
  // Load question counts (from quiz_questions tagged with the module's key) in one pass
  const modules = data || [];
  const keys = modules.map((m) => m.key);
  const countMap = {};
  if (keys.length) {
    const { data: qs } = await supabase
      .from("quiz_questions")
      .select("tags")
      .eq("archived", false);
    (qs || []).forEach((row) => {
      const tags = Array.isArray(row.tags) ? row.tags : [];
      keys.forEach((k) => { if (tags.includes(k)) countMap[k] = (countMap[k] || 0) + 1; });
    });
  }
  return { ok: true, modules: modules.map((m) => ({ ...m, question_count: countMap[m.key] || 0 })) };
}

/* Create a new module (non-ride typically). Ride modules pre-exist. */
export async function createModule(payload) {
  const { data, error } = await supabase
    .from("training_modules").insert(payload).select().single();
  if (error) return { ok: false, message: error.message };
  return { ok: true, module: data };
}

/* Update a module. Supports both content edits (body_md) and workflow flag edits. */
export async function updateModule(id, patch) {
  const update = { ...patch, updated_at: new Date().toISOString() };
  const { data, error } = await supabase
    .from("training_modules").update(update).eq("id", id).select().single();
  if (error) return { ok: false, message: error.message };
  return { ok: true, module: data };
}

export async function archiveModule(id) {
  return updateModule(id, { archived: true });
}

/* Load quiz questions for a module (by module_key, since quiz_questions.tags is the pivot).
   For ride modules this is exactly the ride-scoped questions. For non-ride modules,
   questions get tagged with the module key. */
export async function loadModuleQuestions(moduleKey) {
  const { data, error } = await supabase
    .from("quiz_questions")
    .select("*")
    .eq("archived", false);
  if (error) return { ok: false, message: error.message };
  const filtered = (data || []).filter((q) => {
    const tags = Array.isArray(q.tags) ? q.tags : [];
    return tags.includes(moduleKey);
  });
  return { ok: true, questions: filtered };
}

/* Assignments (per employee) */
export async function loadModuleAssignments(opts = {}) {
  // opts: { employeeId?, moduleId? }
  let q = supabase.from("module_assignments").select("*").order("assigned_at", { ascending: false });
  if (opts.employeeId) q = q.eq("employee_id", opts.employeeId);
  if (opts.moduleId) q = q.eq("module_id", opts.moduleId);
  const { data, error } = await q;
  if (error) return { ok: false, message: error.message };
  return { ok: true, assignments: data || [] };
}

export async function createModuleAssignment(payload) {
  // payload: { module_id, employee_id, due_at }
  const { data, error } = await supabase
    .from("module_assignments").insert(payload).select().single();
  if (error) return { ok: false, message: error.message };
  return { ok: true, assignment: data };
}

export async function updateModuleAssignment(id, patch) {
  const { data, error } = await supabase
    .from("module_assignments").update(patch).eq("id", id).select().single();
  if (error) return { ok: false, message: error.message };
  return { ok: true, assignment: data };
}

export async function deleteModuleAssignment(id) {
  const { error } = await supabase.from("module_assignments").delete().eq("id", id);
  if (error) return { ok: false, message: error.message };
  return { ok: true };
}

/* Quizzes (per assignment) */
export async function loadModuleQuiz(assignmentId) {
  const { data, error } = await supabase
    .from("module_quizzes")
    .select("*")
    .eq("assignment_id", assignmentId)
    .maybeSingle();
  if (error && error.code !== "PGRST116") return { ok: false, message: error.message };
  return { ok: true, quiz: data || null };
}

export async function createModuleQuiz(payload) {
  const { data, error } = await supabase
    .from("module_quizzes").insert(payload).select().single();
  if (error) return { ok: false, message: error.message };
  return { ok: true, quiz: data };
}

export async function updateModuleQuiz(id, patch) {
  const { data, error } = await supabase
    .from("module_quizzes").update(patch).eq("id", id).select().single();
  if (error) return { ok: false, message: error.message };
  return { ok: true, quiz: data };
}

/* Optional PDF attachments — same bucket + policies as before */
export async function uploadModuleDoc(file, moduleKey) {
  const ext = (file.name.split(".").pop() || "pdf").toLowerCase();
  const path = `${moduleKey}/${Date.now()}.${ext}`;
  const { data, error } = await supabase.storage
    .from(MODULE_DOCS_BUCKET)
    .upload(path, file, { upsert: false, contentType: file.type || "application/pdf" });
  if (error) return { ok: false, message: error.message };
  return { ok: true, path: data.path };
}

export async function getModuleDocUrl(storagePath, expiresInSeconds = 3600) {
  const { data, error } = await supabase.storage
    .from(MODULE_DOCS_BUCKET)
    .createSignedUrl(storagePath, expiresInSeconds);
  if (error) return { ok: false, message: error.message };
  return { ok: true, url: data.signedUrl };
}

/* ============================================================
   RIDE ATTESTATIONS

   One signed training certification per employee, per ride.
   These talk to the ride_attestations table directly rather than going
   through syncDiff — attestations are append-only and don't belong in the
   diffed state blob.
   ============================================================ */

/* Load signed certifications. Pass an employeeId to scope to one person,
   or omit it to load all (for reporting). */
export async function loadRideAttestations(employeeId) {
  let q = supabase
    .from("ride_attestations")
    .select("id, employee_id, ride, level_at_signing, attestation, signed_at")
    .order("signed_at", { ascending: false });

  if (employeeId) q = q.eq("employee_id", employeeId);

  const { data, error } = await q;
  if (error) return { ok: false, message: error.message };
  return { ok: true, attestations: data || [] };
}

/* Record one signed certification. The unique (employee_id, ride) constraint
   means a duplicate submit is a no-op rather than a second row — we treat
   code 23505 as success so a double-tap doesn't surface a scary error. */
export async function createRideAttestation({
  employee_id,
  ride,
  level_at_signing,
  attestation,
}) {
  const { data, error } = await supabase
    .from("ride_attestations")
    .insert({
      employee_id,
      ride,
      level_at_signing,
      attestation,
      signed_at: attestation?.signed_at || new Date().toISOString(),
    })
    .select()
    .single();

  if (error) {
    if (error.code === "23505") return { ok: true, attestation: null, duplicate: true };
    return { ok: false, message: error.message };
  }
  return { ok: true, attestation: data };
}

/* ============================================================
   POLICY LIBRARY

   policies + policy_audiences are editable by AGM/GM+.
   policy_views and policy_acknowledgments are append-only (no UPDATE/DELETE
   RLS) — a signature against v1 stays on file as evidence for v1.
   Like ride attestations, these talk to Supabase directly rather than
   going through syncDiff.
   ============================================================ */

const POLICY_COLS =
  "id, key, title, category, body_html, effective_date, requires_acknowledgment, " +
  "published, version, created_at, updated_at, " +
  "audiences:policy_audiences(kind, target, must_acknowledge, due_date, assigned_at)";

/* Load policies with their audience rows attached as `audiences`.
   Staff only ever get published rows (RLS enforces this too). */
export async function loadPolicies({ includeUnpublished = false } = {}) {
  let q = supabase.from("policies").select(POLICY_COLS).order("title", { ascending: true });
  if (!includeUnpublished) q = q.eq("published", true);
  const { data, error } = await q;
  if (error) return { ok: false, message: error.message };
  return {
    ok: true,
    policies: (data || []).map((p) => ({ ...p, audiences: p.audiences || [] })),
  };
}

export async function createPolicy(fields) {
  const { data, error } = await supabase
    .from("policies")
    .insert({ ...fields, version: 1 })
    .select(POLICY_COLS)
    .single();
  if (error) {
    if (error.code === "23505") return { ok: false, message: `A policy with key "${fields.key}" already exists.` };
    return { ok: false, message: error.message };
  }
  return { ok: true, policy: data };
}

/* bumpVersion: content changed materially — everyone required to sign is
   asked again for the new version. Old signatures are kept. */
export async function updatePolicy(id, fields, { bumpVersion = false } = {}) {
  const patch = { ...fields };
  if (bumpVersion) {
    const { data: cur, error: e1 } = await supabase
      .from("policies").select("version").eq("id", id).single();
    if (e1) return { ok: false, message: e1.message };
    patch.version = (cur?.version || 1) + 1;
  }
  const { data, error } = await supabase
    .from("policies")
    .update(patch)
    .eq("id", id)
    .select(POLICY_COLS)
    .single();
  if (error) return { ok: false, message: error.message };
  return { ok: true, policy: data };
}

/* Replace a policy's assignments with exactly `rows`:
   [{ kind: "all"|"department"|"job_title"|"group"|"employee", target, must_acknowledge, due_date }]
   kind "all" uses target "*". */
export async function setPolicyAudience(policyId, rows) {
  const { error: delErr } = await supabase
    .from("policy_audiences").delete().eq("policy_id", policyId);
  if (delErr) return { ok: false, message: delErr.message };
  if (!rows || rows.length === 0) return { ok: true };
  const { error } = await supabase
    .from("policy_audiences")
    .insert(rows.map((r) => ({
      policy_id: policyId,
      kind: r.kind,
      target: String(r.target),
      must_acknowledge: !!r.must_acknowledge,
      due_date: r.due_date || null,
    })));
  if (error) return { ok: false, message: error.message };
  return { ok: true };
}

/* Pass employeeId to scope to one person; omit for reporting (manager+). */
export async function loadPolicyAcknowledgments(employeeId) {
  let q = supabase
    .from("policy_acknowledgments")
    .select("id, policy_id, employee_id, version, attestation, signed_at")
    .order("signed_at", { ascending: false });
  if (employeeId) q = q.eq("employee_id", employeeId);
  const { data, error } = await q;
  if (error) return { ok: false, message: error.message };
  return { ok: true, acknowledgments: data || [] };
}

/* Unique (policy_id, employee_id, version): a double-tap is a no-op. */
export async function createPolicyAcknowledgment({ policy_id, employee_id, version, attestation }) {
  const { data, error } = await supabase
    .from("policy_acknowledgments")
    .insert({
      policy_id,
      employee_id,
      version,
      attestation,
      signed_at: attestation?.signed_at || new Date().toISOString(),
    })
    .select()
    .single();
  if (error) {
    if (error.code === "23505") return { ok: true, acknowledgment: null, duplicate: true };
    return { ok: false, message: error.message };
  }
  return { ok: true, acknowledgment: data };
}

/* Fire-and-forget read log. Never throws. */
export async function recordPolicyView({ policy_id, employee_id, version }) {
  try {
    const { error } = await supabase
      .from("policy_views")
      .insert({ policy_id, employee_id: employee_id || null, version });
    if (error) return { ok: false, message: error.message };
    return { ok: true };
  } catch (e) {
    return { ok: false, message: e.message };
  }
}

export async function loadPolicyViews(policyId) {
  let q = supabase
    .from("policy_views")
    .select("id, policy_id, employee_id, version, viewed_at")
    .order("viewed_at", { ascending: false });
  if (policyId) q = q.eq("policy_id", policyId);
  const { data, error } = await q;
  if (error) return { ok: false, message: error.message };
  return { ok: true, views: data || [] };
}

/* Text of a policy as it stood at a given version (for printing an older
   signed copy). Snapshots are written by a database trigger. */
export async function loadPolicyVersion(policyId, version) {
  const { data, error } = await supabase
    .from("policy_versions")
    .select("policy_id, version, title, category, body_html, effective_date, requires_acknowledgment, saved_at")
    .eq("policy_id", policyId)
    .eq("version", version)
    .maybeSingle();
  if (error) return { ok: false, message: error.message };
  return { ok: true, version: data };
}

/* ============================================================
   EMPLOYEE GROUPS — custom named groups used to assign policies.
   Staff can only read their own memberships (RLS); managers see all.
   ============================================================ */
export async function loadEmployeeGroups() {
  const { data, error } = await supabase
    .from("employee_groups")
    .select("id, name, description, created_at, members:employee_group_members(employee_id)")
    .order("name", { ascending: true });
  if (error) return { ok: false, message: error.message };
  return {
    ok: true,
    groups: (data || []).map((g) => ({ ...g, memberIds: (g.members || []).map((m) => m.employee_id) })),
  };
}

export async function createEmployeeGroup({ name, description }) {
  const { data, error } = await supabase
    .from("employee_groups")
    .insert({ name: name.trim(), description: description?.trim() || null })
    .select()
    .single();
  if (error) {
    if (error.code === "23505") return { ok: false, message: `A group named "${name}" already exists.` };
    return { ok: false, message: error.message };
  }
  return { ok: true, group: data };
}

export async function updateEmployeeGroup(id, { name, description }) {
  const { error } = await supabase
    .from("employee_groups")
    .update({ name: name.trim(), description: description?.trim() || null })
    .eq("id", id);
  if (error) {
    if (error.code === "23505") return { ok: false, message: `A group named "${name}" already exists.` };
    return { ok: false, message: error.message };
  }
  return { ok: true };
}

/* Deleting a group also removes any policy assignments that pointed at it. */
export async function deleteEmployeeGroup(id) {
  const { error: aErr } = await supabase
    .from("policy_audiences").delete().eq("kind", "group").eq("target", String(id));
  if (aErr) return { ok: false, message: aErr.message };
  const { error } = await supabase.from("employee_groups").delete().eq("id", id);
  if (error) return { ok: false, message: error.message };
  return { ok: true };
}

/* Replace a group's membership with exactly `employeeIds`. */
export async function setGroupMembers(groupId, employeeIds) {
  const { error: delErr } = await supabase
    .from("employee_group_members").delete().eq("group_id", groupId);
  if (delErr) return { ok: false, message: delErr.message };
  const ids = Array.from(new Set(employeeIds || []));
  if (ids.length === 0) return { ok: true };
  const { error } = await supabase
    .from("employee_group_members")
    .insert(ids.map((employee_id) => ({ group_id: groupId, employee_id })));
  if (error) return { ok: false, message: error.message };
  return { ok: true };
}

/* ============================================================
   CAREER GOALS
   career_goals + career_goal_requirements = the catalog (AGM/GM+ edit).
   employee_goals = goals an employee picked (employee or manager).
   employee_goal_signoffs = manager sign-offs for 'manual' requirements.
   ============================================================ */
export async function loadGoalCatalog({ includeInactive = false } = {}) {
  let q = supabase
    .from("career_goals")
    .select("id, title, description, sort_order, active, requirements:career_goal_requirements(id, kind, target, level, label, sort_order)")
    .order("sort_order", { ascending: true })
    .order("title", { ascending: true });
  if (!includeInactive) q = q.eq("active", true);
  const { data, error } = await q;
  if (error) return { ok: false, message: error.message };
  return {
    ok: true,
    goals: (data || []).map((g) => ({
      ...g,
      requirements: (g.requirements || []).slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    })),
  };
}

export async function saveGoal({ id, title, description, active = true, sort_order = 0 }) {
  const row = { title: title.trim(), description: description?.trim() || null, active, sort_order };
  const q = id
    ? supabase.from("career_goals").update(row).eq("id", id).select().single()
    : supabase.from("career_goals").insert(row).select().single();
  const { data, error } = await q;
  if (error) {
    if (error.code === "23505") return { ok: false, message: `A goal called "${title}" already exists.` };
    return { ok: false, message: error.message };
  }
  return { ok: true, goal: data };
}

export async function deleteGoal(id) {
  const { error } = await supabase.from("career_goals").delete().eq("id", id);
  if (error) return { ok: false, message: error.message };
  return { ok: true };
}

/* Make a goal's requirements exactly `rows` ([{ id?, kind, target, level, label }]),
   in order. Existing rows are updated in place (so manual sign-offs survive),
   new ones inserted, and removed ones deleted. */
export async function setGoalRequirements(goalId, rows) {
  const { data: existing, error: e1 } = await supabase
    .from("career_goal_requirements").select("id").eq("goal_id", goalId);
  if (e1) return { ok: false, message: e1.message };
  const keep = new Set(rows.filter((r) => r.id).map((r) => r.id));
  const toDelete = (existing || []).map((r) => r.id).filter((id) => !keep.has(id));
  if (toDelete.length) {
    const { error } = await supabase.from("career_goal_requirements").delete().in("id", toDelete);
    if (error) return { ok: false, message: error.message };
  }
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const row = {
      goal_id: goalId,
      kind: r.kind,
      target: r.kind === "manual" ? null : String(r.target || ""),
      level: r.kind === "ride_level" ? r.level : null,
      label: r.label?.trim() || null,
      sort_order: (i + 1) * 10,
    };
    const { error } = r.id
      ? await supabase.from("career_goal_requirements").update(row).eq("id", r.id)
      : await supabase.from("career_goal_requirements").insert(row);
    if (error) return { ok: false, message: error.message };
  }
  return { ok: true };
}

/* Pass employeeId for one person; omit for everyone (manager+). */
export async function loadEmployeeGoals({ employeeId } = {}) {
  let q = supabase.from("employee_goals").select("employee_id, goal_id, added_at, added_by");
  if (employeeId) q = q.eq("employee_id", employeeId);
  const { data, error } = await q;
  if (error) return { ok: false, message: error.message };
  return { ok: true, employeeGoals: data || [] };
}

export async function addEmployeeGoal(employeeId, goalId, addedBy) {
  const { error } = await supabase
    .from("employee_goals")
    .insert({ employee_id: employeeId, goal_id: goalId, added_by: addedBy || null });
  if (error && error.code !== "23505") return { ok: false, message: error.message };
  return { ok: true };
}

export async function removeEmployeeGoal(employeeId, goalId) {
  const { error } = await supabase
    .from("employee_goals").delete().eq("employee_id", employeeId).eq("goal_id", goalId);
  if (error) return { ok: false, message: error.message };
  return { ok: true };
}

export async function loadGoalSignoffs({ employeeId } = {}) {
  let q = supabase.from("employee_goal_signoffs").select("requirement_id, employee_id, signed_by, signed_at, note");
  if (employeeId) q = q.eq("employee_id", employeeId);
  const { data, error } = await q;
  if (error) return { ok: false, message: error.message };
  return { ok: true, signoffs: data || [] };
}

export async function createGoalSignoff({ requirement_id, employee_id, signed_by, note }) {
  const { error } = await supabase
    .from("employee_goal_signoffs")
    .insert({ requirement_id, employee_id, signed_by: signed_by || null, note: note || null });
  if (error && error.code !== "23505") return { ok: false, message: error.message };
  return { ok: true };
}

export async function deleteGoalSignoff(requirement_id, employee_id) {
  const { error } = await supabase
    .from("employee_goal_signoffs").delete().eq("requirement_id", requirement_id).eq("employee_id", employee_id);
  if (error) return { ok: false, message: error.message };
  return { ok: true };
}

/* All training-quiz rows (manager views). Pass employeeId to scope to one person. */
export async function loadModuleQuizzes({ employeeId } = {}) {
  let q = supabase
    .from("module_quizzes")
    .select("id, assignment_id, employee_id, module_id, questions, status, score, attempts, missed_topics, sent_date, completed_date, answers, attempt_history")
    .order("sent_date", { ascending: false });
  if (employeeId) q = q.eq("employee_id", employeeId);
  const { data, error } = await q;
  if (error) return { ok: false, message: error.message };
  return { ok: true, quizzes: data || [] };
}
